import Swal from "sweetalert2";
import { isAxiosError } from "axios";
import store from "src/page/store";
import { setShowSubscriptionModal } from "src/page/plans/plansSlice";
import { AUTH_ERROR_CODE, AuthApiError } from "src/network/authService";
import { PresignedPostError } from "./presignedUtils";

interface ErrorConfig {
  title: string;
  message: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  showCancelButton?: boolean;
  onConfirm?: () => void;
}

export const errorMapping: Record<string, ErrorConfig> = {
  LIMIT_EXCEEDED: {
    title: "Limit Exceeded",
    message: "Your subscription plan limits the action. Please upgrade your plan to proceed.",
    confirmButtonText: "Upgrade Now",
    cancelButtonText: "Cancel",
    showCancelButton: true,
    onConfirm: () => {
      const dispatch = store.dispatch;

      dispatch(setShowSubscriptionModal(true));
    }
  },
  ENTITLEMENT_NOT_ACTIVE: {
    title: "Subscription Required",
    message: "You haven't purchased a subscription yet. Please purchase a subscription plan to proceed.",
    confirmButtonText: "View Plans",
    cancelButtonText: "Cancel",
    showCancelButton: true,
    onConfirm: () => {
      const dispatch = store.dispatch;

      dispatch(setShowSubscriptionModal(true));
    }
  }
};

// Service error codes whose toast needs clearer copy than the backend message.
export const errorCodeMessages: Partial<Record<string, string>> = {
  CARD_EXPIRED: "This card has expired, so it can't be connected.",
  FREQUENCY_WITH_AGE_WINDOW:
    "Repeat reminders on a weekly, fortnightly or every-N-days schedule aren't available yet. Choose Daily, or try again later."
};

export const isUnauthorizedError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error ?? "");

  return /(unauthori[sz]ed|not\s*authori[sz]ed|forbidden|not\s*allowed|access\s*denied|401|403)/i.test(message);
};

export const isAbortError = (error: unknown): boolean => error instanceof DOMException && error.name === "AbortError";

// S3 answers 403 once a presigned POST has expired or its policy no longer matches.
export const isPresignedPostRejected = (error: unknown): boolean =>
  error instanceof PresignedPostError && error.status === 403;

export const isNotFoundError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error ?? "");

  return /(not\s*found|404)/i.test(message);
};

export const isConflictError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error ?? "");

  return /conflict|version|optimistic/i.test(message);
};

const parseTriggerError = (error: unknown): { code?: string; message?: string } | null => {
  const message = error instanceof Error ? error.message : String(error ?? "");
  const payload = message.match(/\{[\s\S]*\}/)?.[0];

  if (!payload) return null;

  try {
    const parsed = JSON.parse(payload);

    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

// Lambda:Unhandled errors carry the service error as a JSON string in `message`.
export const getErrorPayloadCode = (error: unknown): string | undefined => {
  const code = parseTriggerError(error)?.code;

  return typeof code === "string" ? code : undefined;
};

export const isInvalidFileExtensionError = (error: unknown): boolean =>
  getErrorPayloadCode(error) === "INVALID_FILE_EXTENSION";

export const getTriggerErrorMessage = (error: unknown, fallback: string): string => {
  const message = error instanceof Error ? error.message : fallback;
  const inner = parseTriggerError(error)?.message;

  return typeof inner === "string" && inner ? inner : message;
};

export const isAuthApiError = (error: unknown): error is AuthApiError => error instanceof AuthApiError;

export const getAuthErrorCode = (error: unknown): string | undefined =>
  isAuthApiError(error) ? error.code : undefined;

// Only a 401 (NO_SESSION) means the session is gone; 403s (rate limit, origin) and 5xx must not clear state.
export const isSessionGoneError = (error: unknown): boolean => isAuthApiError(error) && error.status === 401;

// The BFF answers 403 { code: MFA_ENROLMENT_REQUIRED } for every GraphQL call while the session is gated.
export const isMfaEnrolmentRequiredError = (error: unknown): boolean => {
  if (isAuthApiError(error)) return error.code === AUTH_ERROR_CODE.MFA_ENROLMENT_REQUIRED;
  if (!isAxiosError(error) || error.response?.status !== 403) return false;

  return error.response.data?.code === AUTH_ERROR_CODE.MFA_ENROLMENT_REQUIRED;
};

export const SESSION_EXPIRED_MESSAGE = "Your session has expired. Please sign in again.";

const AUTH_ERROR_MESSAGES: Partial<Record<string, string>> = {
  [AUTH_ERROR_CODE.INVALID_CREDENTIALS]: "Invalid email or password",
  [AUTH_ERROR_CODE.NO_SESSION]: SESSION_EXPIRED_MESSAGE,
  [AUTH_ERROR_CODE.NOT_AUTHORIZED]: "You're not authorised to do that.",
  [AUTH_ERROR_CODE.FORBIDDEN]: "We couldn't verify this request. Please reload the page and try again.",
  [AUTH_ERROR_CODE.FORBIDDEN_ORIGIN]: "We couldn't verify this request. Please reload the page and try again.",
  [AUTH_ERROR_CODE.TOO_MANY_REQUESTS]: "Too many attempts. Please wait a moment and try again.",
  [AUTH_ERROR_CODE.USER_NOT_CONFIRMED]: "Please verify your email before signing in.",
  [AUTH_ERROR_CODE.PASSWORD_RESET_REQUIRED]: "You need to reset your password before signing in.",
  [AUTH_ERROR_CODE.CODE_MISMATCH]: "That code isn't right. Please check it and try again.",
  [AUTH_ERROR_CODE.INVALID_CODE]: "That code isn't right. Please check it and try again.",
  [AUTH_ERROR_CODE.CODE_EXPIRED]: "That code has expired. Please request a new one.",
  [AUTH_ERROR_CODE.INVALID_PASSWORD]: "That password doesn't meet the password policy.",
  [AUTH_ERROR_CODE.CHALLENGE_EXPIRED]: "Your sign-in attempt timed out. Please sign in again.",
  [AUTH_ERROR_CODE.UNSUPPORTED_CHALLENGE]: "This sign-in method isn't supported yet. Please contact support.",
  [AUTH_ERROR_CODE.CODE_DELIVERY_FAILED]: "We couldn't send the code. Please try again."
};

// User-facing copy per backend auth code; falls back to the server message, then the caller's default.
export const getAuthErrorMessage = (error: unknown, fallback: string): string => {
  if (isAuthApiError(error)) return AUTH_ERROR_MESSAGES[error.code] ?? error.message ?? fallback;

  return error instanceof Error && error.message ? error.message : fallback;
};

const SSO_ERROR_MESSAGES: Partial<Record<string, string>> = {
  SSO_STATE_EXPIRED: "Your single sign-on attempt timed out. Please try again.",
  nonce_mismatch: "We couldn't verify your single sign-on attempt. Please try again from this page.",
  missing_code: "Your sign-in provider didn't return a sign-in code. Please try again.",
  SSO_EXCHANGE_FAILED: "We couldn't complete your single sign-on. Please try again, or contact support if it continues."
};

export const getSsoErrorMessage = (code: string): string =>
  SSO_ERROR_MESSAGES[code] ??
  "We couldn't complete your single sign-on. Please try again, or contact support if the problem continues.";

export const isSignUpEmailTakenError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error ?? "");
  const code = parseTriggerError(error)?.code ?? getAuthErrorCode(error);

  return (
    code === "EMAIL_ALREADY_EXISTS" || code === AUTH_ERROR_CODE.ALIAS_EXISTS || /email already exists/i.test(message)
  );
};

export const isSignUpUsernameTakenError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error ?? "");
  const name = error instanceof Error ? error.name : "";
  const code = parseTriggerError(error)?.code ?? getAuthErrorCode(error);

  return (
    code === "USERNAME_ALREADY_EXISTS" ||
    code === AUTH_ERROR_CODE.USERNAME_EXISTS ||
    name === "UsernameExistsException" ||
    /username already exists/i.test(message)
  );
};

export const getErrorMessage = (error: unknown, fallback: string): string => {
  const err = error as
    | { response?: { data?: { message?: string; error?: string; errors?: { message?: string }[] } }; message?: string }
    | undefined;

  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.errors?.[0]?.message ||
    err?.message ||
    fallback
  );
};

export const showErrorAlert = (errorType: string, fallbackMessage: string) => {
  const errorConfig = errorMapping[errorType];

  if (errorConfig) {
    Swal.fire({
      title: errorConfig.title,
      text: errorConfig.message,
      icon: "warning",
      showCancelButton: errorConfig.showCancelButton ?? false,
      confirmButtonText: errorConfig.confirmButtonText ?? "OK",
      cancelButtonText: errorConfig.cancelButtonText ?? "Cancel"
    }).then((result) => {
      if (result.isConfirmed && errorConfig.onConfirm) {
        errorConfig.onConfirm();
      }
    });
  } else {
    Swal.fire({
      title: "Error",
      text: fallbackMessage,
      icon: "error",
      confirmButtonText: "OK"
    });
  }
};
